import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import {
  ThemeConfig,
  defaultThemeConfig,
  generateTheme,
  ColorTheme,
  AccessibilityMode,
  TextSize,
  AppearanceMode,
} from "@/constants/themes";

interface ThemeContextType {
  themeConfig: ThemeConfig;
  currentTheme: ReturnType<typeof generateTheme>;
  updateColorTheme: (theme: ColorTheme) => void;
  updateAccessibilityMode: (mode: AccessibilityMode) => void;
  updateTextSize: (size: TextSize) => void;
  updateAppearanceMode: (mode: AppearanceMode) => void;
  resetToDefault: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(defaultThemeConfig);
  const [currentTheme, setCurrentTheme] = useState(() =>
    generateTheme(defaultThemeConfig)
  );

  // Check system preference for dark mode
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (themeConfig.appearanceMode === "system") {
        const newConfig = { ...themeConfig, isDarkMode: e.matches };
        setThemeConfig(newConfig);
      }
    };

    // Set initial system theme
    if (themeConfig.appearanceMode === "system") {
      const newConfig = { ...themeConfig, isDarkMode: mediaQuery.matches };
      setThemeConfig(newConfig);
    }

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [themeConfig.appearanceMode]);

  // Update current theme when config changes
  useEffect(() => {
    setCurrentTheme(generateTheme(themeConfig));

    // Apply theme to document
    if (themeConfig.isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [themeConfig]);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("snapshift-admin-theme");
    if (savedTheme) {
      try {
        const parsedTheme = JSON.parse(savedTheme);
        setThemeConfig(parsedTheme);
      } catch (error) {
        console.error("Failed to parse saved theme:", error);
      }
    }
  }, []);

  const saveThemeConfig = (config: ThemeConfig) => {
    localStorage.setItem("snapshift-admin-theme", JSON.stringify(config));
  };

  const updateColorTheme = (colorTheme: ColorTheme) => {
    const newConfig = { ...themeConfig, colorTheme };
    setThemeConfig(newConfig);
    saveThemeConfig(newConfig);
  };

  const updateAccessibilityMode = (accessibilityMode: AccessibilityMode) => {
    const newConfig = { ...themeConfig, accessibilityMode };
    setThemeConfig(newConfig);
    saveThemeConfig(newConfig);
  };

  const updateTextSize = (textSize: TextSize) => {
    const newConfig = { ...themeConfig, textSize };
    setThemeConfig(newConfig);
    saveThemeConfig(newConfig);
  };

  const updateAppearanceMode = (appearanceMode: AppearanceMode) => {
    let isDarkMode = themeConfig.isDarkMode;

    if (appearanceMode === "light") {
      isDarkMode = false;
    } else if (appearanceMode === "dark") {
      isDarkMode = true;
    } else if (appearanceMode === "system") {
      isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
    }

    const newConfig = { ...themeConfig, appearanceMode, isDarkMode };
    setThemeConfig(newConfig);
    saveThemeConfig(newConfig);
  };

  const resetToDefault = () => {
    setThemeConfig(defaultThemeConfig);
    saveThemeConfig(defaultThemeConfig);
  };

  const value: ThemeContextType = {
    themeConfig,
    currentTheme,
    updateColorTheme,
    updateAccessibilityMode,
    updateTextSize,
    updateAppearanceMode,
    resetToDefault,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
